import type { CatalogCategory } from '@/lib/api/catalog/catalog-api'
import {
    useEntityAccess,
    useResourceVisibility,
} from '@/lib/hooks/use-entity-access'
import { useAuth } from '@/store/hooks'
import { useMemo } from 'react'

/**
 * Recurso do menu que corresponde a cada categoria do catálogo. É por ele que a
 * categoria aparece ou não: quem não tem "Exames" no menu também não vê a aba, nem
 * o cadastro de exame.
 */
export const CATALOG_CATEGORY_RESOURCE: Record<
    CatalogCategory,
    { module: string; resource: string }
> = {
    CONSULTATION: { module: 'bookings', resource: 'consultations' },
    EXAM: { module: 'bookings', resource: 'exams' },
    PROCEDURE: { module: 'bookings', resource: 'orders' },
    TELECONSULTATION: { module: 'bookings', resource: 'teleconsultations' },
    PACKAGE: { module: 'bookings', resource: 'packages' },
    PRODUCT: { module: 'commerce', resource: 'products' },
}

/** O assistente do catálogo só existe para a organização da plataforma. */
export const CATALOG_ASSISTANT_ORGANIZATION_ID = 1

/**
 * O que o catálogo pode exibir para o usuário logado, com a MESMA regra do menu
 * lateral. Divergir do menu é o que fazia um tenant que só vê Pedidos e Pacotes
 * enxergar abas de Exame e Consulta aqui.
 */
export function useCatalogAccess() {
    const { user } = useAuth()
    const isResourceVisible = useResourceVisibility()
    const { canSeeProfessionals, canSeePartners } = useEntityAccess()

    const isCategoryAllowed = (category: CatalogCategory): boolean => {
        const { module, resource } = CATALOG_CATEGORY_RESOURCE[category]
        return isResourceVisible(module, resource)
    }

    const allowedKey = (
        Object.keys(CATALOG_CATEGORY_RESOURCE) as CatalogCategory[]
    )
        .filter(isCategoryAllowed)
        .join(',')
    // Memo pela lista em texto: o array é dependência de efeitos e de filtros.
    const allowedCategories = useMemo(
        () => (allowedKey ? (allowedKey.split(',') as CatalogCategory[]) : []),
        [allowedKey]
    )

    return {
        allowedCategories,
        isCategoryAllowed,
        canSeeProfessionals,
        canSeePartners,
        canUseAssistant:
            user?.organizationId === CATALOG_ASSISTANT_ORGANIZATION_ID,
    }
}
